import React, { useMemo, useState } from 'react';
import { Dialog } from '@mui/material';
import { IconButton } from '@mui/material';
import Collapse from '@mui/material/Collapse';
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import StatusIconText from '../../../../components/StatusIconText';
import { backEnvelopeStatusToFront, frontEnvelopeStatusToStyle, frontEnvelopeFolderStatusToStyle } from '../../../../utils/utils';
import { EventsTimeline, buildSignerEvents, eventTypeLabels, stepLabels } from '../../../Envelope/EnvelopePage/SignerSignatureEventsDialog/SignerSignatureEventsDialog';

const ENVELOPE_DONE_STATUSES = ["completed", "signed"]
const ENVELOPES_PHASE_STATUS = "collecting"
const FOLDER_ACCESS_STEPS = ["login", "authentication_code"]

const HIDDEN_FOLDER_STATUSES = ["draft"]
const HIDDEN_ENVELOPE_FRONT_STATUSES = ["created", "dossiers_in_progress"]
const HIDDEN_SIGNER_STATUSES = ["submitted", "analyzing", "analyzed"]
const HIDDEN_SIGNER_STEP_PREFIX = "folder_"

const stepEventTypeLabels = {
    "creation": "Criação",
    "validation": "Validação",
    "resend": "Reenvio",
    "pre_sent": "Pré-envio",
    "change_signer": "Troca de assinante",
}

const folderStatusLabel = (status) => (frontEnvelopeFolderStatusToStyle[status] || {}).text || status

const envelopeStatusLabel = (status) => (frontEnvelopeStatusToStyle[backEnvelopeStatusToFront[status]] || {}).text || status

const personalDocumentLabel = (eventData) => {
    const template = ((eventData || {}).ocr || {}).template || ""
    if (template.endsWith("_front")) return "Documento pessoal - Frente"
    if (template.endsWith("_back")) return "Documento pessoal - Verso"
    return "Documento pessoal"
}

const folderStepEventLabel = (stepEvent) => {
    const eventData = stepEvent.event_data || {}
    const details = []
    if (eventData.device_reset) details.push("Reset de dispositivo")
    if (stepEventTypeLabels[eventData.event_type]) details.push(stepEventTypeLabels[eventData.event_type])
    if (eventData.source === "api") details.push("via API")
    let label = stepLabels[stepEvent.step] || stepEvent.step
    if (stepEvent.step === "personal_document") label = personalDocumentLabel(eventData)
    return details.length > 0 ? `${label} · ${details.join(" · ")}` : label
}

const isHiddenSignerEvent = (signerEvent) => {
    if (signerEvent.type === 'status') return HIDDEN_SIGNER_STATUSES.includes(signerEvent.enum)
    if (signerEvent.type === 'step') return signerEvent.enum.startsWith(HIDDEN_SIGNER_STEP_PREFIX)
    return false
}

const buildEnvelopeEvents = (envelope) => {
    const events = []
    for (const statusEvent of (envelope.envelope_status_events || [])) {
        if (HIDDEN_ENVELOPE_FRONT_STATUSES.includes(backEnvelopeStatusToFront[statusEvent.status])) continue
        events.push({
            type: 'status',
            typeLabel: 'Status do envelope',
            label: envelopeStatusLabel(statusEvent.status),
            event_date: statusEvent.event_date,
        })
    }
    const signers = (envelope.signers || []).filter((signer) => !signer.deactivated_on)
    for (const signer of signers) {
        for (const signerEvent of buildSignerEvents(signer)) {
            if (isHiddenSignerEvent(signerEvent)) continue
            if (signerEvent.type === 'link') {
                signerEvent.label = 'Primeiro acesso ao envelope'
            }
            if (signerEvent.enum === 'personal_document') {
                signerEvent.label = personalDocumentLabel(signerEvent.event_data)
            }
            signerEvent.typeLabel = signerEvent.type === 'status' ? 'Status do assinante' : eventTypeLabels[signerEvent.type]
            if (signers.length > 1) {
                signerEvent.typeLabel = `${signerEvent.typeLabel} · ${signer.name}`
            }
            events.push(signerEvent)
        }
    }
    events.sort((a, b) => new Date(a.event_date) - new Date(b.event_date))
    return events
}

const EnvelopeEventsItem = ({ envelope }) => {
    const [open, setOpen] = useState(false)
    const events = useMemo(() => buildEnvelopeEvents(envelope), [envelope])

    return (
        <div style={{ border: '1px solid #e3e3e3', borderRadius: '6px', marginBottom: '8px', background: '#fff' }}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', font: 'inherit' }}
            >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                <span style={{ flex: 1, fontSize: '14px', minWidth: 0 }}>
                    <strong>{envelope.subject || envelope.id || envelope.envelope_key}</strong>
                </span>
                {envelope.status === "on_signature" ?
                    <StatusIconText
                        id={`${envelope.envelope_key}-events-decision`}
                        envelope_decision_status={envelope.decision}
                        type="envelope_folder_decision"
                    />
                    :
                    <StatusIconText
                        id={`${envelope.envelope_key}-events-status`}
                        envelope_status={envelope.status}
                        rule_analysis_status={envelope.rule_analysis_status}
                        type="envelope_status"
                    />}
            </button>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <div style={{ padding: '10px 16px 12px 16px', borderTop: '1px solid #ececec' }}>
                    {events.length > 0 ?
                        <EventsTimeline events={events} />
                        : <p style={{ paddingBottom: '10px' }}>Nenhum evento disponível para este envelope</p>}
                </div>
            </Collapse>
        </div>
    )
}

function EnvelopeFolderEventsDialog(props) {

    const {
        open,
        setOpen,
        folder,
    } = props

    const events = []
    for (const statusEvent of (folder.envelope_folder_status_events || [])) {
        if (HIDDEN_FOLDER_STATUSES.includes(statusEvent.status)) continue
        events.push({
            type: 'status',
            typeLabel: 'Status da pasta',
            label: folderStatusLabel(statusEvent.status),
            event_date: statusEvent.event_date,
        })
    }
    for (const stepEvent of (folder.envelope_folder_step_events || [])) {
        events.push({
            type: 'step',
            typeLabel: FOLDER_ACCESS_STEPS.includes(stepEvent.step) ? 'Acesso à pasta' : 'Etapa de coleta',
            label: folderStepEventLabel(stepEvent),
            event_date: stepEvent.event_date,
        })
    }
    events.sort((a, b) => new Date(a.event_date) - new Date(b.event_date))

    const envelopes = folder.envelopes || []
    if (envelopes.length > 0) {
        const doneEnvelopes = envelopes.filter((envelope) => ENVELOPE_DONE_STATUSES.includes(envelope.status)).length
        const envelopesPhaseEvent = (folder.envelope_folder_status_events || []).find((statusEvent) => statusEvent.status === ENVELOPES_PHASE_STATUS)
        const envelopesEvent = {
            type: 'envelopes',
            typeLabel: 'Envelopes',
            label: `Aguardando envelopes (${doneEnvelopes} de ${envelopes.length} concluídos)`,
            dotColor: '#e8a317',
            hideConnector: true,
            content: (
                <div style={{ margin: '8px 0' }}>
                    {envelopes.map((envelope) => (
                        <EnvelopeEventsItem
                            key={envelope.envelope_key}
                            envelope={envelope}
                        />
                    ))}
                </div>
            ),
        }
        // O bloco de envelopes entra logo depois do evento em que a pasta passa a aguardar os envelopes
        let insertIndex = events.length
        if (envelopesPhaseEvent) {
            const phaseIndex = events.findIndex((event) => event.type === 'status' && event.event_date === envelopesPhaseEvent.event_date)
            insertIndex = phaseIndex + 1
        }
        events.splice(insertIndex, 0, envelopesEvent)
    }

    return (
        <Dialog
            open={open}
            onClose={() => setOpen(false)}
            fullWidth
            maxWidth={'md'}
        >
            <div className="dialog__column-container">
                <header style={{ marginBottom: '10px' }}>
                    <h1 style={{ fontSize: '22px', color: "#0b1f82" }}>Eventos da pasta</h1>
                    <IconButton
                        onClick={() => setOpen(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                </header>
                <div style={{ marginBottom: '20px' }}>
                    <p>
                        <span style={{ fontSize: '16px' }}><strong>Pasta:</strong></span>
                        <span style={{ fontSize: '16px', marginLeft: '10px' }}>{folder.subject}</span>
                    </p>
                </div>
                {
                    events.length > 0 ?
                        <div style={{ padding: '10px 30px 0px 30px' }}>
                            <EventsTimeline events={events} />
                        </div>
                        : <div>Nenhum evento disponível para esta pasta</div>
                }
            </div>
        </Dialog>
    )
}

export default EnvelopeFolderEventsDialog
